import React, { useEffect, useState } from 'react'
import {
  FaCalendar,
  FaUsers,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaHourglassHalf,
  FaTrash,
  FaCar,
  FaHotel,
  FaUserTie,
  FaMapMarkedAlt,
  FaPrint,
  FaDownload,
} from 'react-icons/fa'
import bookingService from '../services/bookingService'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import Loader from '../components/Loader'
import PriceBreakup from '../components/PriceBreakup'
import { formatDate } from '../utils/helpers'
import SEO from '../utils/SEO'
import Button from '../components/ui/Button'

const Bookings = () => {
  const [bookings, setBookings] = useState([])
  const [filteredBookings, setFilteredBookings] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('all')
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelling, setCancelling] = useState(false)

  useEffect(() => {
    loadBookings()
  }, [])

  useEffect(() => {
    filterBookings()
  }, [activeTab, bookings])

  const loadBookings = async () => {
    try {
      setLoading(true)
      const data = await bookingService.getUserBookings()
      setBookings(data.bookings || [])
    } catch (error) {
      toast.error('Error loading bookings')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const filterBookings = () => {
    const now = new Date()
    let filtered = []

    switch (activeTab) {
      case 'upcoming':
        filtered = bookings.filter(
          (b) => new Date(b.start_date) > now && ['pending', 'confirmed'].includes(b.status),
        )
        break
      case 'completed':
        filtered = bookings.filter((b) => b.status === 'completed')
        break
      case 'cancelled':
        filtered = bookings.filter((b) => b.status === 'cancelled')
        break
      default:
        filtered = bookings
    }

    setFilteredBookings(filtered)
  }

  const handleCancelClick = (booking) => {
    setSelectedBooking(booking)
    setShowCancelModal(true)
  }

  const handleCancelBooking = async () => {
    if (!cancelReason.trim()) {
      toast.error('Please provide a cancellation reason')
      return
    }

    try {
      setCancelling(true)
      await bookingService.cancelBooking(selectedBooking.id, { reason: cancelReason })
      toast.success('Booking cancelled successfully. Admin has been notified.')
      setShowCancelModal(false)
      setCancelReason('')
      setSelectedBooking(null)
      loadBookings()
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error cancelling booking')
    } finally {
      setCancelling(false)
    }
  }

  const getStatusBadge = (status) => {
    const badges = {
      pending: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        icon: FaHourglassHalf,
        label: 'Pending',
      },
      confirmed: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        icon: FaCheckCircle,
        label: 'Confirmed',
      },
      completed: {
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        icon: FaCheckCircle,
        label: 'Completed',
      },
      cancelled: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        icon: FaTimesCircle,
        label: 'Cancelled',
      },
    }

    const badge = badges[status] || badges.pending
    const Icon = badge.icon

    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${badge.bg} ${badge.text}`}
      >
        <Icon className="mr-1" />
        {badge.label}
      </span>
    )
  }

  const canCancelBooking = (booking) => {
    const startDate = new Date(booking.start_date)
    const now = new Date()
    return startDate > now && ['pending', 'confirmed'].includes(booking.status)
  }

  const handlePrint = (booking) => {
    const printHTML = buildInvoiceHTML(booking)
    const blob = new Blob([printHTML], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const win = window.open(url, '_blank')
    if (win) {
      win.onafterprint = () => URL.revokeObjectURL(url)
    }
  }

  const handleDownloadPdf = async (bookingId) => {
    try {
      const blob = await bookingService.getInvoicePdf(bookingId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `invoice-booking-${bookingId}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Failed to download invoice PDF')
    }
  }

  const buildInvoiceHTML = (booking) => {
    const priceBreakup = booking.price_breakup
    const serviceRemarks = booking.service_remarks

    let servicesHTML = ''
    if (booking.services && booking.services.length > 0) {
      servicesHTML = booking.services
        .map((s) => {
          let line = `<li>${s.name} (${s.type})${s.quantity > 1 ? ` x${s.quantity}` : ''}${s.price ? ` — ₹${Number(s.price).toLocaleString('en-IN')}` : ''}</li>`
          if (serviceRemarks) {
            const remark = typeof serviceRemarks === 'object' ? serviceRemarks[s.type] : null
            if (typeof remark === 'string' && remark) {
              line += `<li style="margin-left:16px;color:#6b7280;font-size:12px;">Remark: ${remark.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</li>`
            }
          }
          return line
        })
        .join('')
      servicesHTML = `<div class="section"><div class="section-title">Selected Services</div><ul>${servicesHTML}</ul></div>`
    }

    let guestsHTML = ''
    if (booking.guests && booking.guests.length > 0) {
      guestsHTML = booking.guests
        .map(
          (g, i) =>
            `<tr><td>${i + 1}</td><td>${(g.full_name || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td><td>${g.age}</td><td>${g.gender}</td><td>${g.id_type && g.id_type !== 'none' ? `${g.id_type}: ${(g.id_number || 'N/A').replace(/</g, '&lt;').replace(/>/g, '&gt;')}` : '—'}</td></tr>`,
        )
        .join('')
      guestsHTML = `
        <div class="section">
          <div class="section-title">Guest Details</div>
          <table style="width:100%;border-collapse:collapse;">
            <thead><tr style="background:#f9fafb;text-align:left;"><th style="padding:8px;">#</th><th style="padding:8px;">Name</th><th style="padding:8px;">Age</th><th style="padding:8px;">Gender</th><th style="padding:8px;">ID</th></tr></thead>
            <tbody>${guestsHTML}</tbody>
          </table>
        </div>`
    }

    let priceHTML = ''
    if (priceBreakup) {
      let rows = ''
      if (priceBreakup.tour) {
        rows += `<div class="price-row"><span>Tour: ${(priceBreakup.tour.name || booking.tour_title || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</span><span>₹${Number((priceBreakup.tour.adult_total || 0) + (priceBreakup.tour.child_total || 0)).toLocaleString('en-IN')}</span></div>`
      }
      if (priceBreakup.services) {
        priceBreakup.services.forEach((s) => {
          rows += `<div class="price-row"><span>${(s.name || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')} (${s.type})</span><span>₹${Number(s.cost).toLocaleString('en-IN')}</span></div>`
        })
      }
      rows += `<div class="price-row"><span>Subtotal</span><span>₹${Number(priceBreakup.subtotal || 0).toLocaleString('en-IN')}</span></div>`
      if (priceBreakup.discount) {
        rows += `<div class="price-row" style="color:#059669;"><span>Discount (${(priceBreakup.discount.code || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')})</span><span>-₹${Number(priceBreakup.discount.amount).toLocaleString('en-IN')}</span></div>`
      }
      rows += `<div class="price-row price-total"><span>Total</span><span>₹${Number(priceBreakup.total || 0).toLocaleString('en-IN')}</span></div>`
      priceHTML = `<div class="section"><div class="section-title">Payment Summary</div><div class="price-section">${rows}</div></div>`
    } else {
      let rows = `<div class="price-row"><span>Subtotal</span><span>₹${Number(booking.total_amount || 0).toLocaleString('en-IN')}</span></div>`
      if (Number(booking.discount_amount) > 0) {
        rows += `<div class="price-row" style="color:#059669;"><span>Discount</span><span>-₹${Number(booking.discount_amount).toLocaleString('en-IN')}</span></div>`
      }
      if (Number(booking.admin_discount) > 0) {
        rows += `<div class="price-row" style="color:#7c3aed;"><span>Admin Discount</span><span>-₹${Number(booking.admin_discount).toLocaleString('en-IN')}</span></div>`
      }
      rows += `<div class="price-row price-total"><span>Total</span><span>₹${Number(booking.final_amount || booking.total_amount || 0).toLocaleString('en-IN')}</span></div>`
      priceHTML = `<div class="section"><div class="section-title">Payment Summary</div><div class="price-section">${rows}</div></div>`
    }

    return `<!DOCTYPE html><html><head><title>Invoice — Booking #${booking.id}</title><style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Segoe UI',Arial,sans-serif;padding:20px;background:#f5f5f5}
.invoice{max-width:800px;margin:0 auto;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 4px 20px rgba(0,0,0,.1)}
.header{background:linear-gradient(135deg,#10b981,#059669);color:#fff;padding:30px 40px}
.header h1{font-size:24px;margin-bottom:4px}
.header p{opacity:.9;font-size:13px}
.badge{display:inline-block;background:rgba(255,255,255,.2);padding:6px 16px;border-radius:20px;margin-top:10px;font-weight:600}
.body{padding:30px 40px}
.section{margin-bottom:20px}
.section-title{font-size:13px;text-transform:uppercase;letter-spacing:1px;color:#6b7280;margin-bottom:10px;font-weight:600}
.detail-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.detail-item{padding:10px 14px;background:#f9fafb;border-radius:8px}
.detail-item .label{font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:.5px}
.detail-item .value{font-size:14px;font-weight:600;color:#111827;margin-top:2px}
.price-section{background:#f0fdf4;padding:16px;border-radius:8px;border:1px solid #bbf7d0}
.price-row{display:flex;justify-content:space-between;padding:6px 0;font-size:13px}
.price-total{font-size:18px;font-weight:700;color:#059669;border-top:2px solid #10b981;padding-top:10px;margin-top:6px}
table td,table th{padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:12px}
ul{list-style:disc;padding-left:20px;font-size:13px;line-height:1.8}
.footer{text-align:center;padding:16px 40px 24px;color:#9ca3af;font-size:11px;border-top:1px solid #e5e7eb}
@media print{body{padding:0;background:#fff}.invoice{box-shadow:none}.no-print{display:none!important}}
</style></head><body>
<div class="invoice">
  <div class="header">
    <h1>Himachal Yatra</h1>
    <p>Booking Invoice</p>
    <div class="badge">${(booking.status || 'pending').toUpperCase()}</div>
  </div>
  <div class="body">
    <div class="section"><div class="section-title">Booking Information</div>
      <div class="detail-grid">
        <div class="detail-item"><div class="label">Booking ID</div><div class="value">#${booking.id}</div></div>
        <div class="detail-item"><div class="label">Booking Type</div><div class="value">${(booking.booking_type || 'tour').charAt(0).toUpperCase() + (booking.booking_type || 'tour').slice(1)}</div></div>
        <div class="detail-item"><div class="label">Start Date</div><div class="value">${booking.start_date ? new Date(booking.start_date).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}</div></div>
        <div class="detail-item"><div class="label">Duration</div><div class="value">${booking.number_of_days || 'N/A'} days</div></div>
        <div class="detail-item"><div class="label">Adults</div><div class="value">${booking.num_adults || 0}</div></div>
        <div class="detail-item"><div class="label">Children</div><div class="value">${booking.num_children || 0}</div></div>
      </div>
    </div>
    ${guestsHTML}
    ${servicesHTML}
    ${priceHTML}
    ${booking.special_requests ? `<div class="section"><div class="section-title">Special Requests</div><p style="font-size:13px;">${booking.special_requests.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p></div>` : ''}
  </div>
  <div class="footer">
    <p>This is a computer-generated invoice and does not require a signature.</p>
    <p style="margin-top:4px;">© ${new Date().getFullYear()} Himachal Yatra. All rights reserved.</p>
  </div>
</div>
<div class="no-print" style="text-align:center;margin-top:20px;">
  <button onclick="window.print()" style="background:#10b981;color:#fff;border:none;padding:12px 32px;border-radius:8px;font-size:16px;cursor:pointer;">Print / Save as PDF</button>
</div>
</body></html>`
  }

  const tabs = [
    { id: 'all', label: 'All Bookings', count: bookings.length },
    {
      id: 'upcoming',
      label: 'Upcoming',
      count: bookings.filter(
        (b) => new Date(b.start_date) > new Date() && ['pending', 'confirmed'].includes(b.status),
      ).length,
    },
    {
      id: 'completed',
      label: 'Completed',
      count: bookings.filter((b) => b.status === 'completed').length,
    },
    {
      id: 'cancelled',
      label: 'Cancelled',
      count: bookings.filter((b) => b.status === 'cancelled').length,
    },
  ]

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <SEO title="My Bookings" noindex />
      <ToastContainer />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-display font-bold text-gray-900 mb-4">My Bookings</h1>
          <p className="text-gray-600 text-lg">Manage and track all your travel bookings</p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-4 mb-8">
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'tab-active' : 'tab-inactive'}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              <span
                className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-white text-primary-600' : 'bg-gray-200 text-gray-700'
                }`}
              >
                {tab.count}
              </span>
            </Button>
          ))}
        </div>

        {/* Bookings List */}
        {loading ? (
          <Loader />
        ) : filteredBookings.length === 0 ? (
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-12 text-center">
            <p className="text-gray-500 text-lg">No bookings found</p>
            <p className="text-gray-400 mt-2">Start planning your next adventure!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredBookings.map((booking) => (
              <div
                key={booking.id}
                className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg border border-white/30 p-6 hover:shadow-xl transition-shadow"
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                  {/* Left Section - Main Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          Booking #{booking.id}
                        </h3>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {getStatusBadge(booking.status)}
                          {booking.payment_status && (
                            <span
                              className={`px-3 py-1 rounded-full text-sm font-semibold ${
                                booking.payment_status === 'paid'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-orange-100 text-orange-800'
                              }`}
                            >
                              {booking.payment_status === 'paid' ? 'Paid' : 'Payment Pending'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Booking Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center text-gray-600">
                        <FaCalendar className="mr-2 text-primary-600" />
                        <span>
                          <span className="font-semibold">Start:</span>{' '}
                          {formatDate(booking.start_date)}
                        </span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <FaClock className="mr-2 text-primary-600" />
                        <span>
                          <span className="font-semibold">Duration:</span>{' '}
                          {booking.number_of_days || 'N/A'} days
                        </span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <FaUsers className="mr-2 text-primary-600" />
                        <span>
                          <span className="font-semibold">Persons:</span>{' '}
                          {booking.number_of_persons || booking.num_adults + booking.num_children}
                        </span>
                      </div>
                    </div>

                    {/* Selected Services */}
                    <div className="border-t border-gray-200 pt-4">
                      <h4 className="font-semibold text-gray-900 mb-3">
                        {booking.booking_type === 'service'
                          ? 'Booked Services:'
                          : 'Selected Services:'}
                      </h4>
                      <div className="space-y-2">
                        {booking.tour_id && (
                          <div className="flex items-center text-gray-700">
                            <FaMapMarkedAlt className="mr-2 text-primary-600" />
                            <span>{booking.tour_title || 'Tour Package'}</span>
                          </div>
                        )}

                        {/* Display all services from booking.services array */}
                        {booking.services &&
                          booking.services.length > 0 &&
                          booking.services.map((service, idx) => {
                            const serviceIcons = {
                              car: FaCar,
                              hotel: FaHotel,
                              homestay: FaHotel,
                              guide: FaUserTie,
                              activity: FaMapMarkedAlt,
                              pickup_drop: FaCar,
                            }
                            const ServiceIcon = serviceIcons[service.type] || FaMapMarkedAlt

                            return (
                              <div
                                key={`${service.id}-${idx}`}
                                className="flex items-center justify-between text-gray-700"
                              >
                                <div className="flex items-center">
                                  <ServiceIcon className="mr-2 text-primary-600" />
                                  <span>{service.name}</span>
                                </div>
                                {service.quantity > 1 && (
                                  <span className="text-sm text-gray-500">x{service.quantity}</span>
                                )}
                              </div>
                            )
                          })}

                        {!booking.tour_id &&
                          (!booking.services || booking.services.length === 0) && (
                            <p className="text-gray-500 text-sm">No services selected</p>
                          )}
                      </div>
                    </div>

                    {/* Guest Details */}
                    {booking.guests && booking.guests.length > 0 && (
                      <div className="border-t border-gray-200 pt-4 mt-4">
                        <h4 className="font-semibold text-gray-900 mb-3">
                          <FaUsers className="inline mr-2 text-primary-600" />
                          Travellers ({booking.guests.length})
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {booking.guests.map((guest, idx) => (
                            <div
                              key={guest.id || idx}
                              className="flex items-center text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2"
                            >
                              <span className="font-medium mr-2">{idx + 1}.</span>
                              <span>{guest.full_name}</span>
                              <span className="mx-1 text-gray-400">·</span>
                              <span className="text-gray-500">
                                {guest.age}y, {guest.gender}
                                {guest.is_child ? ' (Child)' : ''}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Special Requests */}
                    {booking.special_requests && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600">
                          <span className="font-semibold">Special Requests:</span>{' '}
                          {booking.special_requests}
                        </p>
                      </div>
                    )}

                    {/* Service Remarks */}
                    {booking.service_remarks &&
                      typeof booking.service_remarks === 'object' &&
                      Object.keys(booking.service_remarks).length > 0 && (
                        <div className="mt-4 p-3 bg-indigo-50 rounded-lg">
                          <p className="text-sm font-semibold text-indigo-900 mb-1">
                            Service Remarks:
                          </p>
                          <div className="space-y-1 text-sm text-gray-700">
                            {booking.service_remarks.car && (
                              <p>
                                <span className="font-medium">Car:</span>{' '}
                                {booking.service_remarks.car}
                              </p>
                            )}
                            {booking.service_remarks.guide && (
                              <p>
                                <span className="font-medium">Guide:</span>{' '}
                                {booking.service_remarks.guide}
                              </p>
                            )}
                            {booking.service_remarks.hotel && (
                              <p>
                                <span className="font-medium">Hotel:</span>{' '}
                                {booking.service_remarks.hotel}
                              </p>
                            )}
                            {booking.service_remarks.camp_site && (
                              <p>
                                <span className="font-medium">Camp Site:</span>{' '}
                                {booking.service_remarks.camp_site}
                              </p>
                            )}
                            {booking.service_remarks.activities?.map((a) => (
                              <p key={a.service_id}>
                                <span className="font-medium">Activity #{a.service_id}:</span>{' '}
                                {a.remarks}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}

                    {/* Admin Notes */}
                    {booking.admin_notes && (
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-900">
                          <span className="font-semibold">Admin Notes:</span> {booking.admin_notes}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Right Section - Pricing & Actions */}
                  <div className="lg:w-80 space-y-4">
                    {/* Pricing */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      {booking.price_breakup ? (
                        <PriceBreakup breakup={booking.price_breakup} />
                      ) : (
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Subtotal:</span>
                            <span className="font-semibold">
                              ₹
                              {parseFloat(
                                booking.total_amount || booking.total_price || 0,
                              ).toLocaleString()}
                            </span>
                          </div>
                          {booking.discount_amount > 0 && (
                            <div className="flex justify-between text-sm text-green-600">
                              <span>Discount:</span>
                              <span className="font-semibold">
                                -₹{parseFloat(booking.discount_amount).toLocaleString()}
                              </span>
                            </div>
                          )}
                          {booking.admin_discount > 0 && (
                            <div className="flex justify-between text-sm text-purple-600">
                              <span>Admin Discount:</span>
                              <span className="font-semibold">
                                -₹{parseFloat(booking.admin_discount).toLocaleString()}
                              </span>
                            </div>
                          )}
                          <div className="border-t border-gray-300 pt-2 mt-2">
                            <div className="flex justify-between">
                              <span className="font-bold text-gray-900">Final Amount:</span>
                              <span className="font-bold text-primary-600 text-xl">
                                ₹
                                {parseFloat(
                                  booking.final_price || booking.final_amount || 0,
                                ).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Button
                          variant="secondary"
                          className="flex-1"
                          icon={<FaPrint />}
                          onClick={() => handlePrint(booking)}
                        >
                          Print
                        </Button>
                        <Button
                          variant="secondary"
                          className="flex-1"
                          icon={<FaDownload />}
                          onClick={() => handleDownloadPdf(booking.id)}
                        >
                          PDF
                        </Button>
                      </div>
                      {canCancelBooking(booking) && (
                        <Button
                          variant="danger"
                          fullWidth
                          icon={<FaTrash />}
                          onClick={() => handleCancelClick(booking)}
                        >
                          Cancel Booking
                        </Button>
                      )}
                      {booking.status === 'confirmed' && (
                        <p className="text-sm text-gray-500 text-center">
                          ✓ Confirmation email sent
                        </p>
                      )}
                      {booking.status === 'pending' && (
                        <p className="text-sm text-yellow-600 text-center">
                          <FaHourglassHalf className="inline mr-1" />
                          Waiting for admin confirmation
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Cancel Booking</h2>
            <p className="text-gray-600 mb-4">
              Are you sure you want to cancel booking #{selectedBooking?.id}? This action cannot be
              undone.
            </p>

            <div className="mb-6">
              <label className="label">Cancellation Reason</label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Please provide a reason for cancellation..."
                className="input-field"
                rows="4"
                required
              />
            </div>

            <div className="flex gap-4">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => {
                  setShowCancelModal(false)
                  setCancelReason('')
                  setSelectedBooking(null)
                }}
                disabled={cancelling}
              >
                Keep Booking
              </Button>
              <Button
                variant="danger"
                className="flex-1"
                onClick={handleCancelBooking}
                disabled={cancelling}
                loading={cancelling}
              >
                {cancelling ? 'Cancelling...' : 'Confirm Cancellation'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Bookings
